import { Editor } from '@tiptap/core';
import CharacterCount from '@tiptap/extension-character-count';
import { Link } from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { JSONContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import _ from 'lodash';
import { useMemo, useRef } from 'react';

export interface IUseRichTextEditorParams {
  defaultTextAlignment?: 'left' | 'center' | 'right';
  maxLength?: number;
  onChange: (params: {
    html: string;
    json: JSONContent;
    wordCount: number;
  }) => void;
  placeholder?: string;
  value: JSONContent | string;
}

export const useRichTextEditor = ({
  defaultTextAlignment,
  maxLength,
  onChange,
  placeholder,
  value,
}: IUseRichTextEditorParams) => {
  const extensions = useMemo(() => {
    return _.compact([
      StarterKit.configure({
        code: false,
        codeBlock: false,
        heading: {
          levels: [2, 3],
        },
        hardBreak: false,
        horizontalRule: false,
      }),
      Link.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        defaultAlignment: defaultTextAlignment || 'left',
      }),
      Underline,
      CharacterCount.configure({
        limit: maxLength,
      }),
      Placeholder.configure({
        placeholder,
      }),
    ]);
  }, [maxLength]);

  const editor = useEditor({
    extensions,
    onUpdate: ({ editor }: { editor: Editor }) => {
      onChange({
        html: editor.isEmpty ? undefined : editor.getHTML(),
        json: editor.isEmpty ? undefined : editor.getJSON(),
        wordCount: editor.storage.characterCount.words(),
      });
    },
    content: value,
  });

  const { current: id } = useRef(_.uniqueId('richText'));

  return { editor, id };
};
